/**
 * CKEditor5 → Email-safe HTML
 * Solves:
 *  - CKEditor classes → inline CSS
 *  - hsl() colors → hex (Outlook does not support hsl)
 *  - raw-html-embed wrapper → unwrap
 *  - text-huge, text-big, text-small → font-size inline
 *  - <sup> / <sub> → vertical-align inline (Outlook fix)
 *  - Removes all classes and data-* attributes
 */

// ─── CKEditor classes → inline CSS ───────────────────────────────────────────
const CK_CLASS_MAP = {
  // Font sizes
  'text-tiny': 'font-size:0.7em',
  'text-small': 'font-size:0.85em',
  'text-big': 'font-size:1.4em',
  'text-huge': 'font-size:1.8em',

  // Alignment
  'ck-align-left': 'text-align:left',
  'ck-align-center': 'text-align:center',
  'ck-align-right': 'text-align:right',
  'ck-align-justify': 'text-align:justify',

  // Images
  'image-style-side': 'float:right;margin-left:16px;max-width:50%',
  'image-style-align-left': 'float:left;margin-right:16px',
  'image-style-align-right': 'float:right;margin-left:16px',
  'image-style-align-center': 'display:block;margin:0 auto',
  'image-style-block-align-center': 'display:block;margin:0 auto',

  // Tables
  'ck-table': 'border-collapse:collapse;width:100%',
  'ck-table__cell': 'border:1px solid #ccc;padding:8px',
};

// ─── Base styles per tag ──────────────────────────────────────────────────────
const TAG_BASE_STYLES = {
  table: 'border-collapse:collapse;width:100%;font-family:inherit',
  td: 'padding:8px;vertical-align:top',
  th: 'padding:8px;font-weight:bold;background:#f4f4f4',
  img: 'max-width:100%;height:auto;display:block;border:0',
  a: 'color:#1a73e8;text-decoration:underline',
  p: 'margin:0 0 1em 0',
  ul: 'margin:0 0 1em 0;padding-left:2em',
  ol: 'margin:0 0 1em 0;padding-left:2em',
  li: 'margin-bottom:4px',
  h1: 'font-size:2em;font-weight:bold;margin:0.67em 0',
  h2: 'font-size:1.5em;font-weight:bold;margin:0.75em 0',
  h3: 'font-size:1.17em;font-weight:bold;margin:0.83em 0',
  blockquote: 'border-left:4px solid #ccc;margin:8px 0;padding:8px 16px;color:#666',
  // Outlook fix for sup/sub — avoid "vertical-align: super/sub" as it shifts line-height
  sup: 'vertical-align:super;font-size:0.75em;line-height:0',
  sub: 'vertical-align:sub;font-size:0.75em;line-height:0',
};

// ─── HSL → Hex converter ──────────────────────────────────────────────────────
function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Replace all hsl() and hsla() values in a CSS string with their hex equivalent
function convertHslInStyle(styleStr) {
  if (!styleStr) return styleStr;

  // hsla(H, S%, L%, A) — Outlook ignores alpha anyway
  styleStr = styleStr.replace(
    /hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%(?:\s*,\s*[\d.]+)?\s*\)/gi,
    (_, h, s, l) => hslToHex(parseFloat(h), parseFloat(s), parseFloat(l)),
  );

  return styleStr;
}

// ─── raw-html-embed unwrap ────────────────────────────────────────────────────
// CKEditor wraps raw HTML inside <div class="raw-html-embed">
// For email we only need the content, without the wrapper
function unwrapRawEmbeds(doc) {
  doc.querySelectorAll('.raw-html-embed').forEach((el) => {
    const parent = el.parentNode;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });
}

// ─── Merge CSS strings ────────────────────────────────────────────────────────
function mergeStyles(...parts) {
  const map = new Map();
  parts
    .filter(Boolean)
    .join(';')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((decl) => {
      const i = decl.indexOf(':');
      if (i === -1) return;
      const prop = decl.slice(0, i).trim();
      const val = decl.slice(i + 1).trim();
      if (prop && val) map.set(prop, val);
    });
  return [...map.entries()].map(([k, v]) => `${k}:${v}`).join(';');
}

// ─── Process a single element ─────────────────────────────────────────────────
function processNode(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const tag = node.tagName.toLowerCase();
  const classes = [...node.classList];

  // 1. Styles from CKEditor classes
  const classStyles = classes
    .map((cls) => CK_CLASS_MAP[cls])
    .filter(Boolean)
    .join(';');

  // 2. Base style for the tag
  const baseStyle = TAG_BASE_STYLES[tag] || '';

  // 3. Existing inline style — convert HSL to hex
  const rawExisting = node.getAttribute('style') || '';
  const existingStyle = convertHslInStyle(rawExisting);

  // 4. Merge (base < class < existing)
  const merged = mergeStyles(baseStyle, classStyles, existingStyle);
  if (merged) node.setAttribute('style', merged);

  // 5. Strip classes and data-* attributes
  node.removeAttribute('class');
  [...node.attributes]
    .filter((a) => a.name.startsWith('data-'))
    .forEach((a) => node.removeAttribute(a.name));

  // 6. Recurse into children
  [...node.children].forEach(processNode);
}

// ─── Main function ────────────────────────────────────────────────────────────
export function inlineEmailStyles(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Unwrap CKEditor raw-html-embed divs (e.g. images inserted as raw HTML)
  unwrapRawEmbeds(doc);

  // Process all elements
  processNode(doc.body);

  return doc.body.innerHTML;
}

// ─── CKEditor5 integration ────────────────────────────────────────────────────
export default function getEmailReadyHtml(rawHtml) {
  // const rawHtml = editorInstance.getData();
  return inlineEmailStyles(rawHtml);
}
